import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Avatar,
  LinearProgress,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import colors from "colors/color";
import { Close, PersonOutline } from "@mui/icons-material";
import ImageUploadButton from "../ImageUploadButton";
import { useTranslation } from "react-i18next";

const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${alpha(colors.primary, 0.1)}`,
    overflow: 'visible',
    position: 'relative',
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
  color: 'white',
  padding: theme.spacing(2.5, 3.5),
  borderRadius: '20px 20px 0 0',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 70%)`,
    transform: 'translate(30px, -30px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '60px',
    background: `radial-gradient(circle, ${alpha('#ffffff', 0.08)} 0%, transparent 70%)`,
    transform: 'translate(-20px, 20px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2.5),
    paddingRight: theme.spacing(7)
  }
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(145deg, #ffffff 0%, #fafbff 100%)',
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.3)} 100%)`,
    borderRadius: '16px 16px 0 0',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: alpha('#ffffff', 0.8),
    transition: 'all 0.3s ease',
    border: `1px solid ${alpha(colors.primary, 0.1)}`,
    '&:hover': {
      backgroundColor: '#ffffff',
      boxShadow: `0 4px 12px ${alpha(colors.primary, 0.08)}`,
      '& fieldset': {
        borderColor: alpha(colors.primary, 0.3),
      }
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: `0 6px 20px ${alpha(colors.primary, 0.12)}`,
      '& fieldset': {
        borderColor: colors.primary,
        borderWidth: 2,
      }
    },
    '& fieldset': {
      borderColor: 'transparent',
    }
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins',
    fontWeight: 500,
    color: alpha(colors.primary, 0.7),
    '&.Mui-focused': {
      color: colors.primary,
      fontWeight: 600,
    }
  },
  '& .MuiOutlinedInput-input': {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '14px',
    padding: '14px 16px',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: 16,
  color: colors.primary,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::after': {
    content: '""',
    flex: 1,
    height: '1px',
    background: `linear-gradient(90deg, ${alpha(colors.primary, 0.3)} 0%, transparent 100%)`,
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    marginBottom: theme.spacing(1.5),
  }
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.1, 2.5),
  fontFamily: 'Poppins',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '14px',
  minWidth: '120px',
  transition: 'all 0.3s ease',
  ...(variant === 'primary' ? {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
    color: 'white',
    border: 'none',
    boxShadow: `0 4px 15px ${alpha(colors.primary, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.9)} 0%, ${alpha(colors.primary, 0.7)} 100%)`,
      boxShadow: `0 6px 20px ${alpha(colors.primary, 0.4)}`,
      transform: 'translateY(-1px)',
    }
  } : {
    backgroundColor: alpha(colors.primary, 0.08),
    color: colors.primary,
    border: `1px solid ${alpha(colors.primary, 0.2)}`,
    '&:hover': {
      backgroundColor: alpha(colors.primary, 0.12),
      border: `1px solid ${alpha(colors.primary, 0.3)}`,
      transform: 'translateY(-1px)',
    }
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    fontSize: '13px',
    minWidth: '100px',
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: 'rgba(255, 255, 255, 0.8)',
  backgroundColor: alpha('#ffffff', 0.1),
  backdropFilter: 'blur(10px)',
  '&:hover': {
    backgroundColor: alpha('#ffffff', 0.2),
    color: 'white',
  }
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  background: alpha(colors.primary, 0.1),
  overflow: 'hidden',
  borderRadius: '20px 20px 0 0',
}));

const MUICircularProgress = ({ value, size = 60 }) => {
  const getProgressColor = (progress) => {
    if (progress <= 25) return '#f44336';
    if (progress <= 50) return '#ff9800';
    if (progress <= 75) return '#ffeb3b';
    return '#4caf50';
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={4}
        sx={{
          color: getProgressColor(value),
          filter: `drop-shadow(0 2px 4px ${alpha(getProgressColor(value), 0.3)})`,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{
          color: alpha('#ffffff', 0.2),
          position: 'absolute',
          left: 0,
          zIndex: -1,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: size > 50 ? 11 : 9,
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }}
        >
          {value}%
        </Typography>
      </Box>
    </Box>
  );
};

const CreateSuperAdminDialog = ({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  submitLoading,
  completion,
  title,
  subtitle,
  actionLabel,
  fieldsConfig
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [focusedField, setFocusedField] = useState(null);

  const defaultTitle = title || t('superAdmin.createSuperAdminDialog.createSuperAdmin');
  const defaultSubtitle = subtitle || t('superAdmin.createSuperAdminDialog.addNewSuperAdministrator');
  const defaultActionLabel = actionLabel || t('superAdmin.createSuperAdminDialog.createSuperAdmin');

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const formFields = useMemo(() => (fieldsConfig || [
    {
      section: t('superAdmin.createSuperAdminDialog.accountInformation'),
      icon: <PersonOutline />,
      fields: [
        { name: 'username', label: t('superAdmin.createSuperAdminDialog.username'), required: true },
        { name: 'email', label: t('superAdmin.createSuperAdminDialog.emailAddress'), required: true, type: 'email' },
        { name: 'password', label: t('superAdmin.createSuperAdminDialog.password'), required: true, type: 'password' }
      ]
    },
    {
      section: t('superAdmin.createSuperAdminDialog.personalDetails'),
      icon: <PersonOutline />,
      fields: [
        { name: 'first_name', label: t('superAdmin.createSuperAdminDialog.firstName'), required: true },
        { name: 'last_name', label: t('superAdmin.createSuperAdminDialog.lastName'), required: true }
      ]
    },
    {
      section: t('superAdmin.createSuperAdminDialog.locationAndContact'),
      icon: <PersonOutline />,
      fields: [
        { name: 'address', label: t('superAdmin.createSuperAdminDialog.address') },
        { name: 'city', label: t('superAdmin.createSuperAdminDialog.city') },
        { name: 'country', label: t('superAdmin.createSuperAdminDialog.country') },
        { name: 'postal_code', label: t('superAdmin.createSuperAdminDialog.postalCode') }
      ]
    },
    {
      section: t('superAdmin.createSuperAdminDialog.profileImage'),
      icon: <PersonOutline />,
      fields: [
        { name: 'image', label: t('superAdmin.createSuperAdminDialog.profileImage') }
      ]
    }
  ]), [fieldsConfig, t]);

  return (
    <ModernDialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMdDown}>
      <ProgressIndicator>
        <LinearProgress 
          variant="determinate" 
          value={completion}
          sx={{
            height: '3px',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
              borderRadius: '0 3px 3px 0',
            }
          }}
        />
      </ProgressIndicator>

      <DialogHeader>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          position: 'relative', 
          zIndex: 1,
          pr: { xs: 8, sm: 10, md: 12 }
        }}>
          <Avatar sx={{ 
            bgcolor: alpha('#ffffff', 0.2), 
            color: 'white',
            width: { xs: 40, sm: 48 }, 
            height: { xs: 40, sm: 48 },
            backdropFilter: 'blur(10px)'
          }}>
            <PersonOutline />
          </Avatar>
          <Box sx={{ 
            flex: 1, 
            minWidth: 0, 
            display: 'flex', 
            flexDirection: 'column',
            maxWidth: 'calc(100% - 120px)'
          }}>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontWeight: 700,
              fontSize: { xs: 18, sm: 22, md: 24 },
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              wordBreak: 'break-word',
              lineHeight: { xs: 1.25, sm: 1.2 }
            }}>
              {defaultTitle}
            </Typography>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: { xs: 12, sm: 13, md: 14 },
              color: alpha('#ffffff', 0.8),
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
              overflow: 'visible',
              lineHeight: { xs: 1.45, sm: 1.35, md: 1.3 },
              mt: 0.25
            }}>
              {defaultSubtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          position: 'absolute', 
          top: { xs: 12, sm: 14, md: 16 }, 
          right: { xs: 12, sm: 14, md: 16 }, 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1, md: 1.5 },
          zIndex: 2
        }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            <MUICircularProgress value={completion} size={50} />
            <Typography sx={{
              color: 'white',
              fontSize: 12,
              fontFamily: 'Poppins',
              fontWeight: 500,
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>
              Complete
            </Typography>
          </Box>
          <CloseButton 
            onClick={onClose}
            sx={{
              position: 'static',
              right: 'auto',
              top: 'auto',
              width: { xs: 32, sm: 36, md: 40 },
              height: { xs: 32, sm: 36, md: 40 },
              color: 'rgba(255, 255, 255, 0.9)',
              backgroundColor: alpha('#ffffff', 0.15),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#ffffff', 0.2)}`,
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.25),
                color: 'white',
                transform: 'scale(1.05)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 18, sm: 20, md: 22 }
              }
            }}
          >
            <Close />
          </CloseButton>
        </Box>
        <Box sx={{ 
          display: { xs: 'flex', sm: 'none' },
          justifyContent: 'center',
          alignItems: 'center',
          mt: 2,
          pt: 1,
          borderTop: `1px solid ${alpha('#ffffff', 0.1)}`,
          gap: 1
        }}>
          <MUICircularProgress value={completion} size={32} />
          <Typography sx={{
            color: alpha('#ffffff', 0.8),
            fontSize: 11,
            fontFamily: 'Poppins',
            fontWeight: 500
          }}>
            Complete
          </Typography>
        </Box>
      </DialogHeader>

      <DialogContent sx={{ p: { xs: 2, sm: 3, md: 4 }, maxHeight: isMdDown ? 'unset' : '70vh', overflowY: 'auto' }}>
        {formFields.map((section, sectionIndex) => (
          <FormSection key={sectionIndex}>
            <SectionTitle>
              {section.icon}
              {section.section}
            </SectionTitle>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {section.fields.map((field) => (
                <Grid item xs={12} md={section.fields.length === 1 ? 12 : 6} key={field.name}>
                  {field.name === 'image' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography sx={{ fontFamily: 'Poppins', fontSize: 12, color: alpha(colors.primary, 0.7) }}>
                        {field.label || 'Profile Image'}
                      </Typography>
                      <ImageUploadButton
                        defaultValue={form.image}
                        onImageUpload={(url) => setForm((prev) => ({ ...prev, image: url }))}
                      />
                    </Box>
                  ) : (
                    <StyledTextField
                      fullWidth
                      size="small"
                      type={field.type || 'text'}
                      name={field.name}
                      label={field.label}
                      value={form[field.name]}
                      onChange={handleChange}
                      required={field.required}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField((prev) => (prev === field.name ? null : prev))}
                      InputLabelProps={{
                        shrink: focusedField === field.name || Boolean(form[field.name])
                      }}
                      sx={{
                        '& .MuiInputLabel-asterisk': {
                          color: '#f44336'
                        }
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </FormSection>
        ))}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3, md: 4 }, pt: { xs: 1, sm: 1.5, md: 2 }, gap: 2 }}>
        <ActionButton
          onClick={onClose}
          disabled={submitLoading}
        >
          {t('superAdmin.createSuperAdminDialog.cancel')}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={onSubmit}
          disabled={submitLoading || !form.username || !form.email || !form.password || !form.first_name || !form.last_name}
          sx={{ position: 'relative' }}
        >
          {submitLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                sx={{
                  width: 20,
                  height: 2,
                  '& .MuiLinearProgress-bar': { bgcolor: 'white' }
                }}
              />
              {t('superAdmin.createSuperAdminDialog.creating')}
            </Box>
          ) : defaultActionLabel}
        </ActionButton>
      </DialogActions>
    </ModernDialog>
  );
};

export default CreateSuperAdminDialog;


